import type { Action } from 'app/scripts/models/action';
import type { Card } from 'app/scripts/models/card';
import type { List } from 'app/scripts/models/list';
import type { PendingOrganization } from 'app/scripts/models/PendingOrganization';
import type { Member } from 'app/scripts/models/member';
import type { Notification } from 'app/scripts/models/notification';
import type { NotificationGroup } from 'app/scripts/models/NotificationGroup';
import type { Plugin } from 'app/scripts/models/Plugin';
import type { Reaction } from 'app/scripts/models/reaction';
import type { PayloadQuery } from 'app/scripts/network/payloads';
import type { Board } from 'app/scripts/models/board';
import type { Enterprise } from 'app/scripts/models/enterprise';
import type { Organization } from 'app/scripts/models/organization';

import BluebirdPromise from 'bluebird';
const { Promise } = require('bluebird');
import { Time } from 'app/scripts/lib/time';
import { ninvoke } from 'app/scripts/lib/util/ninvoke';
const { QuickLoad } = require('@trello/quickload');
import type { Callback, QuickloadError } from '@trello/quickload';
import { Analytics } from '@trello/atlassian-analytics';
import { ApiError } from 'app/scripts/network/api-error';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import assert from 'app/scripts/lib/assert';
import { Util } from 'app/scripts/lib/util';
import { ModelCache } from 'app/scripts/db/model-cache';
import _ from 'underscore';
import { waitFor } from './waitFor';

const preloadUncalledSentinel = {};

const quickload = function (
  url: string,
  operationName: string,
  traceId?: string | null,
): BluebirdPromise<[JSON, XMLHttpRequest]> {
  let preload = preloadUncalledSentinel;
  return Promise.fromNode(
    (next: Callback) =>
      (preload = QuickLoad.load(
        url,
        next,
        operationName,
        'model-loader',
        traceId,
        traceId ? Analytics.getTaskForTraceId(traceId) : null,
      )),
  )
    .catch(function (args: QuickloadError) {
      const [statusCode, responseText] = args;
      const apiError = ApiError.fromResponse(statusCode, responseText);
      sendNetworkErrorEvent({
        status: statusCode,
        response: apiError.toString(),
        url,
        operationName,
      });
      return Promise.reject(apiError);
    })
    .tap(() =>
      assert(
        preload !== preloadUncalledSentinel,
        "Promises resolved synchronously; didn't get preload information from QuickLoad",
      ),
    )
    .spread((data: JSON | null, xhr: XMLHttpRequest) => [data, xhr, preload]);
};

const updateServerTime = function (xhr: XMLHttpRequest) {
  let serverTime;
  // eslint-disable-next-line eqeqeq
  if ((serverTime = xhr.getResponseHeader('X-Server-Time')) != null) {
    Time.updateServerTime(parseInt(serverTime, 10));
  }
};

interface LoadApiDataOptions {
  id?: string | null;
  payload?: PayloadQuery;
  path?: string | null;
  metadataHeaderName?: string | null;
  isHeaderLoad?: boolean | null;
  traceId?: string | null;
}

export function loadApiData(
  modelType: 'Member',
  options: LoadApiDataOptions,
): BluebirdPromise<Member>;
export function loadApiData(
  modelType: 'Organization',
  options: LoadApiDataOptions,
): BluebirdPromise<Organization>;
export function loadApiData(
  modelType: 'Board',
  options: LoadApiDataOptions,
): BluebirdPromise<Board>;
export function loadApiData(
  modelType: 'Card',
  options: LoadApiDataOptions,
): BluebirdPromise<Card>;
export function loadApiData(
  modelType: 'Notification',
  options: LoadApiDataOptions,
): BluebirdPromise<Notification>;
export function loadApiData(
  modelType: 'NotificationGroup',
  options: LoadApiDataOptions,
): BluebirdPromise<NotificationGroup>;
export function loadApiData(
  modelType: 'Action',
  options: LoadApiDataOptions,
): BluebirdPromise<Action>;
export function loadApiData(
  modelType: 'Enterprise',
  options: LoadApiDataOptions,
): BluebirdPromise<Enterprise>;
export function loadApiData(
  modelType: 'List',
  options: LoadApiDataOptions,
): BluebirdPromise<List>;
export function loadApiData(
  modelType: 'Plugin',
  options: LoadApiDataOptions,
): BluebirdPromise<Plugin[]>;
export function loadApiData(
  modelType: 'PendingOrganization',
  options: LoadApiDataOptions,
): BluebirdPromise<PendingOrganization>;
export function loadApiData(
  modelType: 'Reaction',
  options: LoadApiDataOptions,
): BluebirdPromise<Reaction>;
export function loadApiData(
  modelType: 'notificationsCount' | 'highlights' | 'upNext' | 'search',
  options: LoadApiDataOptions,
): BluebirdPromise<unknown>;
export function loadApiData(
  modelType: string,
  {
    id,
    payload,
    path,
    metadataHeaderName,
    isHeaderLoad,
    traceId,
  }: LoadApiDataOptions,
): BluebirdPromise<unknown> {
  let { query } = payload || {};
  const { operationName, mappingRules = {} } = payload || {};
  // If we gave a class like Board, convert that to "Board"
  if (!_.isString(modelType)) {
    // We expect an error here because we have no way to tell the signature that this can be any Trello Model. I think
    // we should work towards converging on the `modelType: string` signature and then delete this code once it's safe.
    // @ts-expect-error
    modelType = modelType.prototype.typeName;
  }
  if (typeof path === 'undefined' || path === null) {
    path = `${Util.pluralize(modelType)}/${id}`;
  }

  const invitationTokens = Util.invitationTokens().join(',');
  if (!_.isEmpty(invitationTokens)) {
    // Note that this has to match the behavior of quickload; where
    // invitationTokens is added as the last thing
    query = _.extend({}, query, { invitationTokens });
  }

  const url = QuickLoad.makeUrl(`/1/${path}`, query);

  return quickload(url, operationName ?? `load:${modelType}`, traceId)
    .spread(function (data: JSON, xhr: XMLHttpRequest) {
      updateServerTime(xhr);

      return [data, xhr];
    })
    .tap(function () {
      // We skip this logic when we're running GraphiQL because the header is never loaded in that context.
      if (isHeaderLoad) {
        return;
      } else {
        return new BluebirdPromise(function (resolve) {
          return waitFor('headerData', resolve);
        });
      }
    })
    .spread(function (data: JSON, xhr: XMLHttpRequest) {
      return Promise.try(function () {
        if (data) {
          return ninvoke(
            ModelCache,
            'enqueueDelta',
            modelType,
            data,
            mappingRules,
            query,
          );
        } else {
          return [];
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then(function (models: any) {
        if (!metadataHeaderName) {
          return models;
        }
        const metadata = JSON.parse(
          xhr.getResponseHeader(metadataHeaderName) || '{}',
        );
        return [models, metadata];
      });
    });
}
